/**
 * A full origin chain (wake to the sect choice) as data: the types and the registry. Each origin's chain lives in its
 * own file (`origin<X>.ts`) and is listed once in `FULL_CHAINS`; systems/originFull.ts runs whichever is registered for
 * the player's origin (systems/originStory.ts delegates to it and skips the old stub of data/story/originChains.ts).
 * Progress is kept as `og-done-<quest id>` scene marks, as the stub did. Design: docs/design/2026-10-13-origin-b-chain.md.
 */
import type { QuestDef } from '../quests';
import { ORIGIN_B } from './originB';
import { ORIGIN_C } from './originC';
import { ORIGIN_D } from './originD';

export type XY = readonly [number, number];

/** A person of the chain. `school`: a recruiter (talk offers `joinSchool`). `wanderer`: offers "walk alone". */
export interface FullNpc {
  readonly id: string;
  readonly zone: string;
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  readonly at: XY;
  readonly facing?: 1 | -1;
  /** Said when talked to outside of an objective (turns through the list). */
  readonly idle: readonly string[];
  readonly school?: string;
  readonly wanderer?: boolean;
  /** The `talk` objective target this person counts as (default: its id); each person counts once. */
  readonly talkAs?: string;
  /** Present only from this step on (step id), e.g. freed prisoners or the recruiters. */
  readonly from?: string;
  /** After this step is done the person walks here (the freed prisoners go to the plaza). */
  readonly after?: { readonly step: string; readonly to: XY };
}

/** A place to reach: `mount` = riding it; `water` = standing on water. */
export interface FullSpot {
  readonly at: XY;
  readonly r: number;
  readonly mount?: string;
  readonly water?: boolean;
}

/** A lantern watcher: looks along its facing (x side) and turns every `period` s, warning `warn` s before. */
export interface Watcher {
  readonly at: XY;
  readonly range: number;
  /** Half-width of the lit band across the facing axis (m). */
  readonly band: number;
  readonly period: number;
  readonly warn: number;
  readonly startFacing: 1 | -1;
  /** The watcher's name, title and sprite (default: ยามถือตะเกียง / หันตามเสียงกระแอม / guardOfficer). */
  readonly name?: string;
  readonly title?: string;
  readonly sprite?: string;
}

/** An ordered run of checkpoints (`route` objective, one count each). Watchers or a clock can send it back. */
export interface FullRoute {
  readonly id: string;
  readonly points: readonly { readonly at: XY; readonly r: number }[];
  readonly watchers?: readonly Watcher[];
  readonly resetTo: XY;
  readonly failText: string;
}

/** A person to lead along a path (`escort` objective). Stops when the hero is past `leadRange` or a wave stands. */
export interface FullEscort {
  readonly id: string;
  readonly npc: string;
  readonly path: readonly XY[];
  readonly speed: number;
  readonly leadRange: number;
  readonly waves: readonly { readonly atPathIndex: number; readonly monster: string; readonly n: number }[];
  readonly waitText: string;
}

/** One quest of the chain and what it needs around it. */
export interface FullStep {
  readonly quest: QuestDef;
  readonly zone: string;
  /** Monsters put in when the step starts (taken away when it ends); `name` renames them. */
  readonly spawns?: readonly { readonly monster: string; readonly at: XY; readonly name?: string; readonly sprite?: string }[];
  /** What a person says when talked to for this step's `talk` objective (by npc id). */
  readonly talkLines?: Readonly<Record<string, string>>;
  /** A clock on the whole step: run out and the hero goes back to `failTo` and the step starts over. */
  readonly timer?: { readonly seconds: number; readonly failTo: XY; readonly failText: string };
  /** Talking to this person for the step grants a mount (one only). */
  readonly grantMount?: { readonly npc: string; readonly mount: string };
  /** Learned when the step's talk is done (qinggong id at level 1). */
  readonly grantQinggong?: string;
  /** The "go here" marker while the step runs: a person or a spot id. */
  readonly guide?: { readonly npc?: string; readonly spot?: string };
  /** Where the hero is put when the chain starts (the wake). */
  readonly startAt?: XY;
  /** Objectives count only in order (each waits for the ones before it). */
  readonly ordered?: boolean;
  /**
   * A bout for the step's `duelWin`: talking to `npc` (a chain person) while the step runs starts a fair bout against
   * it in the duel system's style `styleId` (`DUEL_STYLES`) at `level` (0-4); `label` is shown over the bout.
   */
  readonly bout?: { readonly npc: string; readonly styleId: string; readonly level: number; readonly label: string };
}

export interface FullChainDef {
  readonly origin: 'b' | 'c' | 'd';
  readonly npcs: readonly FullNpc[];
  readonly spots: Readonly<Record<string, FullSpot>>;
  readonly routes: Readonly<Record<string, FullRoute>>;
  readonly escorts: Readonly<Record<string, FullEscort>>;
  readonly steps: readonly FullStep[];
  /** The duel step's rival: an NPC of another system found by name in the zone (or a chain NPC id). */
  readonly duelRival?: { readonly name: string };
  readonly lines: {
    readonly wandererPick: string;
    readonly joined: string;
    readonly chosenAlready: string;
    readonly allDone: string;
  };
}

/** The full chains by origin; an origin left out still runs the stub (data/story/originChains.ts). */
export const FULL_CHAINS: Readonly<Partial<Record<'b' | 'c' | 'd', FullChainDef>>> = {
  b: ORIGIN_B,
  c: ORIGIN_C,
  d: ORIGIN_D,
};

export const ogDone = (id: string): string => `og-done-${id}`;
